import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiUser, FiPackage, FiDollarSign, FiSearch, FiCalendar, FiFileText, FiCreditCard, FiShoppingCart } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useFacturas } from '../../hooks/useFacturas';
import { useClientes } from '../../hooks/useClientes';
import { useProductos } from '../../hooks/useProductos';
import { clientesService } from '../../services';

const FacturaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  const {
    createFactura,
    updateFactura,
    getFacturaById,
    isLoading: facturasLoading,
    error: facturasError
  } = useFacturas();

  const { clientes, loadClientes } = useClientes();
  const { productos: allProductos, searchProductos, isLoading: productosLoading } = useProductos();

  const [factura, setFactura] = useState({
    clienteId: '',
    fecha: new Date().toISOString().split('T')[0],
    fechaVencimiento: '',
    metodoPago: 'TERMINOS',
    vendedorId: '',
    notas: '',
    items: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedClienteData, setSelectedClienteData] = useState(null);

  // Estados para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemDiscount, setItemDiscount] = useState(0);

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadFactura();
    }
  }, [id, isEditing]);

  // Efecto para búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      searchProductos(productSearch);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch]);

  // Actualizar resultados cuando cambien los productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      setSearchResults(allProductos);
    }
  }, [allProductos, productSearch]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadFactura = async () => {
    setIsLoading(true);
    const result = await getFacturaById(id);
    if (result.success) {
      setFactura(result.data);
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  // Cargar datos del cliente cuando se selecciona
  const loadClienteData = async (clienteId) => {
    if (!clienteId) {
      setSelectedClienteData(null);
      return;
    }

    try {
      const result = await clientesService.getCliente(clienteId);

      if (result.success) {
        setSelectedClienteData(result.data);

        // Auto-completar método de pago según el cliente
        const metodoPago = result.data.payment_type || 'TERMINOS';

        // Si el cliente tiene términos de pago, calcular fecha de vencimiento
        setFactura(prev => {
          const updatedFactura = {
            ...prev,
            metodoPago: metodoPago
          };

          if (metodoPago === 'TERMINOS' && result.data.payment_terms) {
            const fechaEmision = new Date(prev.fecha);
            const diasTerminos = result.data.payment_terms || 30;
            const fechaVenc = new Date(fechaEmision);
            fechaVenc.setDate(fechaVenc.getDate() + diasTerminos);

            updatedFactura.fechaVencimiento = fechaVenc.toISOString().split('T')[0];
          }

          return updatedFactura;
        });
      } else {
        console.error('Error al cargar datos del cliente:', result.message);
      }
    } catch (err) {
      console.error('Error de conexión al cargar cliente:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFactura(prev => ({
      ...prev,
      [field]: value
    }));

    // Si cambia el cliente, cargar sus datos
    if (field === 'clienteId' && value) {
      loadClienteData(value);
    }

    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.nombreProducto);
    setSearchResults([]);
    setShowDropdown(false); // Cerrar dropdown al seleccionar
  };

  const addItemToFactura = () => {
    const qty = parseFloat(itemQuantity);
    if (!selectedProduct || !qty || qty <= 0) {
      setError('Seleccione un producto y especifique una cantidad válida');
      return;
    }

    // Verificar stock disponible
    if (selectedProduct.stock < qty) {
      setError(`Stock insuficiente. Solo hay ${selectedProduct.stock} unidades disponibles.`);
      return;
    }

    const subtotal = qty * selectedProduct.precio;
    const descuentoMonto = subtotal * (itemDiscount / 100);
    const total = subtotal - descuentoMonto;

    const newItem = {
      productoId: selectedProduct.id,
      nombreProducto: selectedProduct.nombreProducto,
      codigo: selectedProduct.codigo,
      cantidad: qty,
      precioUnitario: selectedProduct.precio,
      descuento: itemDiscount,
      subtotal,
      descuentoMonto,
      total
    };

    setFactura(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setItemDiscount(0);
    setError(null);
  };

  const removeItem = (index) => {
    setFactura(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = factura.items.reduce((sum, item) => sum + item.subtotal, 0);
    const descuento = factura.items.reduce((sum, item) => sum + item.descuentoMonto, 0);
    const total = subtotal - descuento;

    return { subtotal, descuento, impuestos: 0, total };
  };

  const validateForm = () => {
    const errors = {};

    if (!factura.clienteId) {
      errors.clienteId = 'Cliente es requerido';
    }
    if (!factura.fecha) {
      errors.fecha = 'Fecha es requerida';
    }
    if (factura.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Por favor corrija los errores en el formulario');
      return;
    }

    setIsLoading(true);
    setError(null);

    const totals = calculateTotals();
    const facturaData = {
      ...factura,
      clienteId: parseInt(factura.clienteId), // Asegurar que sea número
      subtotal: totals.subtotal,
      impuestos: totals.impuestos,
      descuento: totals.descuento,
      total: totals.total
    };

    console.log('Datos de factura a enviar:', facturaData);

    const result = isEditing
      ? await updateFactura(id, facturaData)
      : await createFactura(facturaData);

    if (result.success) {
      navigate('/system/facturacion');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate('/system/facturacion');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const totals = calculateTotals();

  if (isLoading || facturasLoading) {
    return <LoadingGrid message={isEditing ? "Cargando factura..." : "Preparando formulario..."} />;
  }

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Factura' : 'Nueva Factura'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : 'Crear Factura'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="factura-form">
        {/* Card 1: Información Principal de la Factura - Diseño Premium */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiFileText size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">
                {isEditing ? 'Factura #' + id : 'Nueva Factura'}
              </h3>
              <p className="invoice-header-subtitle">
                Complete los detalles de la factura
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            {/* Cliente */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Cliente *</label>
                <select
                  className={`invoice-field-input ${validationErrors.clienteId ? 'invoice-field-input--error' : ''}`}
                  value={factura.clienteId}
                  onChange={(e) => handleInputChange('clienteId', parseInt(e.target.value) || '')}
                  required
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombreNegocio}
                    </option>
                  ))}
                </select>
                {validationErrors.clienteId && (
                  <span className="invoice-field-error">{validationErrors.clienteId}</span>
                )}
                {selectedClienteData && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#0369a1'
                  }}>
                    <strong>Términos:</strong> {selectedClienteData.payment_type === 'TERMINOS'
                      ? `${selectedClienteData.payment_terms || 30} días`
                      : 'COD (Pago al instante)'}
                    {selectedClienteData.credit_limit > 0 && (
                      <span style={{ marginLeft: '12px' }}>
                        | <strong>Crédito:</strong> ${selectedClienteData.credit_limit?.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Fecha */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Emisión *</label>
                <input
                  type="date"
                  className={`invoice-field-input ${validationErrors.fecha ? 'invoice-field-input--error' : ''}`}
                  value={factura.fecha}
                  onChange={(e) => handleInputChange('fecha', e.target.value)}
                  required
                />
                {validationErrors.fecha && (
                  <span className="invoice-field-error">{validationErrors.fecha}</span>
                )}
              </div>
            </div>

            {/* Fecha Vencimiento */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Vencimiento</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={factura.fechaVencimiento}
                  onChange={(e) => handleInputChange('fechaVencimiento', e.target.value)}
                />
              </div>
            </div>

            {/* Método de Pago */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCreditCard size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Método de Pago</label>
                <select
                  className="invoice-field-input"
                  value={factura.metodoPago}
                  onChange={(e) => handleInputChange('metodoPago', e.target.value)}
                >
                  <option value="TERMINOS">Términos</option>
                  <option value="COD">COD (Pago al instante)</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="EFECTIVO">Efectivo</option>
                </select>
              </div>
            </div>

            {/* Notas - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas adicionales sobre la factura..."
                  value={factura.notas}
                  onChange={(e) => handleInputChange('notas', e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Buscador de Productos - Diseño Moderno */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la factura</p>
            </div>
          </div>

          <div className="product-search-body">
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />
              {productosLoading && (
                <div className="search-combo-loading">
                  <div className="spinner-sm"></div>
                </div>
              )}

              {/* Dropdown de resultados con diseño mejorado */}
              {showDropdown && searchResults.length > 0 && (
                <div className="product-dropdown">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="product-dropdown-item"
                      onClick={() => handleProductSelect(product)}
                    >
                      <div className="product-dropdown-icon">
                        <FiPackage size={20} />
                      </div>
                      <div className="product-dropdown-info">
                        <div className="product-dropdown-name">{product.nombreProducto}</div>
                        <div className="product-dropdown-meta">
                          <span className="meta-code">{product.codigo}</span>
                          <span className="meta-separator">•</span>
                          <span className="meta-stock">Stock: {product.stock}</span>
                        </div>
                      </div>
                      <div className="product-dropdown-price">
                        {formatCurrency(product.precio)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">{selectedProduct.nombreProducto}</h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Código: {selectedProduct.codigo}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Precio: {formatCurrency(selectedProduct.precio)}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Stock: {selectedProduct.stock}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(e.target.value)}
                      min="0.1"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <div className="control-group">
                    <label className="control-label">Descuento %</label>
                    <input
                      type="number"
                      value={itemDiscount}
                      onChange={(e) => setItemDiscount(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToFactura}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Tabla de Factura - Diseño Profesional */}
        {factura.items.length > 0 ? (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon">
                <FiShoppingCart size={24} />
              </div>
              <div className="table-header-content">
                <h3 className="table-header-title">Productos en la Factura</h3>
                <p className="table-header-subtitle">{factura.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead className="invoice-table-head">
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-qty">Cantidad</th>
                    <th className="th-price">Precio Unit.</th>
                    <th className="th-discount">Desc.</th>
                    <th className="th-total">Total</th>
                    <th className="th-actions"></th>
                  </tr>
                </thead>
                <tbody className="invoice-table-body">
                  {factura.items.map((item, index) => (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">{item.nombreProducto}</div>
                            <div className="product-cell-code">{item.codigo}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <span className="qty-badge">{item.cantidad}</span>
                      </td>
                      <td className="td-price">
                        <span className="price-value">{formatCurrency(item.precioUnitario)}</span>
                      </td>
                      <td className="td-discount">
                        {item.descuento > 0 ? (
                          <span className="discount-badge">{item.descuento}%</span>
                        ) : (
                          <span className="no-discount">—</span>
                        )}
                      </td>
                      <td className="td-total">
                        <span className="total-value">{formatCurrency(item.total)}</span>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-icon-delete"
                          onClick={() => removeItem(index)}
                          title="Eliminar producto"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales - Diseño Profesional */}
            <div className="invoice-totals-section">
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Subtotal</span>
                  <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.descuento > 0 && (
                  <div className="totals-row totals-row--discount">
                    <span className="totals-label">Descuento</span>
                    <span className="totals-value">-{formatCurrency(totals.descuento)}</span>
                  </div>
                )}
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Total</span>
                  <span className="totals-value">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>

            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        ) : (
          <div className="invoice-empty-state">
            <div className="empty-state-icon">
              <FiShoppingCart size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la factura
            </p>
            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default FacturaForm;
